import type { PanelProps } from "../panel-props"

import { useSelectSettings } from "../../../hooks/use-select-settings"
import { IS_AUTH } from "../../../modules/auth"
import { useDatabaseLength } from "../../../modules/database-length-store"
import { useSettings } from "../../../modules/settings"
import { SELECT_CORRECT_WORD_BOOKS } from "../../../select-correct-word/settings"
import { Badge } from "../../badge"
import { Select } from "../../select"
import { Slider } from "../../slider"
import { ToggleButton } from "../../toggle-button"
import { SharedSettings } from "../shared-settings.tsx"
import { useToggleAppSetting } from "../toggle-app-setting-hooks.ts"

export function SelectCorrectWordSettings(input: PanelProps) {
  const { appSettings, setAppSettings, setIndexInStorage } = useSettings()
  const databaseLength = useDatabaseLength()

  const {
    onChange: handleSelectCorrectWordBookChange,
    options: selectCorrectWordBookOptions,
  } = useSelectSettings({
    options: SELECT_CORRECT_WORD_BOOKS,
    setValue: newValue=> {
      setAppSettings("book", newValue, "selectCorrectWord")
    },
  })

  const [readAsBookModeIsOn, toggleReadAsBookMode] = useToggleAppSetting(
    "selectCorrectWord",
    "readAsBookMode",
  )
  const [limitQuestionsModeIsOn, toggleLimitQuestionsMode] =
    useToggleAppSetting("selectCorrectWord", "limitQuestionsMode")
  const [hardModeIsOn, toggleHardMode] = useToggleAppSetting(
    "selectCorrectWord",
    "hardMode",
  )
  const book = appSettings.selectCorrectWord.book as IndexesKey
  const currentIndex = Math.min(
    Math.max(appSettings.index[book] ?? 0, 0),
    Math.max(databaseLength - 1, 0),
  )

  return (
    <SharedSettings closeSidebar={input.closeSidebar}>
      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Choices</h3>
        <ToggleButton
          checked={limitQuestionsModeIsOn}
          label="Limit Questions"
          onChange={toggleLimitQuestionsMode}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Reading</h3>
        <ToggleButton
          checked={readAsBookModeIsOn}
          label="Book Mode"
          onChange={toggleReadAsBookMode}
        />
        <ToggleButton
          checked={hardModeIsOn}
          label="Hard Mode"
          onChange={toggleHardMode}
        />
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Source</h3>
        {IS_AUTH && (
          <Select
            initialValue={appSettings.selectCorrectWord.book}
            label={"Selected book"}
            onChange={handleSelectCorrectWordBookChange}
            options={selectCorrectWordBookOptions}
          />
        )}
      </section>

      <div className="sidebar__separator" role="separator" />

      <section className="sidebar__section">
        <h3 className="sidebar__section-title">Position</h3>
        <Slider
          label="Question index"
          max={Math.max(databaseLength - 1, 0)}
          min={0}
          navigation={{ step: 1 }}
          onValueChange={(value)=> setIndexInStorage(book, value)}
          step={1}
          value={currentIndex}
        />
        <div>
          index <Badge value={String(currentIndex)} /> from{" "}
          <Badge value={String(databaseLength - 1)} />
        </div>
      </section>
    </SharedSettings>
  )
}
